import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Archive, WarningOctagon } from "@phosphor-icons/react";

/** Turns a FastAPI detail — string, or {message, blockers:[{message}], offer_archive} — into text. */
export const lifecycleError = (detail) => {
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const parts = [detail.message].filter(Boolean);
    (detail.blockers || detail.unresolved || []).forEach((b) => parts.push(typeof b === "string" ? b : b.message));
    if (detail.offer_archive) parts.push(detail.offer_archive);
    if (parts.length) return parts.join(" ");
  }
  return apiError(detail);
};

export function ArchiveDialog({ open, onOpenChange, kind, record, onDone }) {
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setReason(""); setConfirmed(false); } }, [open]);
  if (!record) return null;

  const label = kind === "students" ? "student" : "staff member";
  const keeps = kind === "students"
    ? "Attendance, family, billing, health, incident, form and audit history are all kept."
    : "Payroll, tax, timecard, classroom and audit history are all kept.";

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/${kind}/${record.id}/archive`, { reason, confirmed });
      toast.success(data.message || "Archived");
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(lifecycleError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg" data-testid="archive-dialog">
        <DialogHeader>
          <DialogTitle>Archive {record.name}?</DialogTitle>
          <DialogDescription>
            This takes the {label} out of every active list and future assignment. Nothing is deleted — {keeps}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-bold">Why are you archiving? (recorded in the audit log)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)}
                   placeholder={kind === "students" ? "Family moved away" : "Left the center"}
                   className="mt-2 h-11 rounded-xl" data-testid="archive-reason" />
          </div>
          <label className="flex items-start gap-3 text-sm font-semibold cursor-pointer">
            <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(Boolean(v))}
                      data-testid="archive-confirm" />
            I understand this removes them from active lists and that the history is kept.
          </label>
          <Button onClick={submit} disabled={busy || !confirmed || reason.trim().length < 2}
                  className="w-full h-11 rounded-full font-bold bg-[#FBBF24] text-[#0F172A] hover:bg-[#F59E0B]"
                  data-testid="archive-submit">
            <Archive size={17} className="mr-2" /> {busy ? "Archiving…" : "Archive"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PermanentDeleteDialog({ open, onOpenChange, kind, record, onDone }) {
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dep, setDep] = useState(null);

  const loadDeps = useCallback(async () => {
    if (!record) return;
    try {
      const { data } = await api.get(`/${kind}/${record.id}/dependencies`);
      setDep(data);
    } catch (e) { toast.error(lifecycleError(e.response?.data?.detail)); }
  }, [kind, record]);

  useEffect(() => {
    if (open) { setName(""); setPassword(""); setConfirmed(false); setDep(null); loadDeps(); }
  }, [open, loadDeps]);

  if (!record) return null;
  const blocked = dep && dep.retention_blockers.length > 0;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/${kind}/${record.id}`, {
        data: { confirm_name: name, current_password: password, confirmed, reason: "Permanent deletion" } });
      toast.success(data.message || "Permanently deleted");
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(lifecycleError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="delete-dialog">
        <DialogHeader>
          <DialogTitle>Permanently delete {record.name}?</DialogTitle>
          <DialogDescription>
            We check every connected record first. Historical records are never deleted.
          </DialogDescription>
        </DialogHeader>

        {!dep && <p className="text-sm text-muted-foreground" data-testid="delete-checking">Checking connected records…</p>}

        {dep && blocked && (
          <div className="rounded-2xl border-2 border-[#EF4444] bg-red-50 p-4" data-testid="delete-blocked">
            <p className="flex items-center gap-2 text-sm font-bold text-red-700">
              <WarningOctagon size={18} weight="bold" /> Permanent deletion is blocked
            </p>
            <ul className="mt-3 space-y-2 text-sm">
              {dep.retention_blockers.map((b) => (
                <li key={b.key} data-testid={`blocker-${b.key}`}>• {b.message}</li>
              ))}
            </ul>
            <p className="mt-3 text-sm font-semibold">
              Keep the record archived instead — that takes them out of every active list while the history stays.
            </p>
          </div>
        )}

        {dep && !blocked && (
          <div className="space-y-4">
            <div className="rounded-2xl border-2 border-[#EF4444] bg-red-50 p-4">
              <p className="text-sm font-bold text-red-700">This cannot be undone.</p>
              <p className="mt-2 text-sm">
                No connected records were found, so this record can be removed from operational use. A tombstone
                and the audit trail are kept.
              </p>
            </div>
            <div>
              <Label className="text-sm font-bold">Type <strong>{record.name}</strong> to confirm</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                     className="mt-2 h-11 rounded-xl" data-testid="delete-confirm-name" />
            </div>
            <div>
              <Label className="text-sm font-bold">Your current password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                     autoComplete="current-password" className="mt-2 h-11 rounded-xl"
                     data-testid="delete-password" />
            </div>
            <label className="flex items-start gap-3 text-sm font-semibold cursor-pointer">
              <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(Boolean(v))}
                        data-testid="delete-confirm-check" />
              I understand this is permanent and cannot be undone.
            </label>
            <Button onClick={submit} disabled={busy || !confirmed || !name || !password}
                    className="w-full h-11 rounded-full font-bold bg-[#EF4444] hover:bg-[#DC2626]"
                    data-testid="delete-submit">
              {busy ? "Deleting…" : "Permanently delete"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
